import svgIcons from '@/assets/svg/icons';
import SvgIcon from '@/components/atoms/SvgIcon';
import { WELLNESS_DATA_MULTIPLIER, WELLNESS_SUB_DOMAINS_DISPLAY_LABELS } from '@/constants';
import { extra, primary, text } from '@/theme/colors/palettes';
import { THealthDomain, THealthDomainWithOverall, TSubdomainConcern } from '@/types/Health.types';
import { Box, Center, HStack, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import SectionContainer from './SectionContainer';
import YourScore from '@/components/organisms/WholenessRingWithOverview/YourScore';
import useSubdomainScreenerInterpretation from '@/hooks/screener/useSubdomainScreenerInterpretation';
import LoadingSection from './LoadingSection';
import CircledExclamation from '@/components/atoms/CircledExclamation';
import SubdomainRecommendationSection from './SubdomainRecommendationSection';

interface Props {
  domain: THealthDomainWithOverall;
  subdomainConcern: TSubdomainConcern;
  resetSelectedSubdomainConcern: () => void;
}

const SubdomainModalContent = ({ domain, subdomainConcern, resetSelectedSubdomainConcern }: Props) => {
  const { data, isLoading } = useSubdomainScreenerInterpretation(
    (domain ?? 'mental') as THealthDomain,
    subdomainConcern?.subdomain,
  );

  const isOther = subdomainConcern.subdomain.startsWith('OTHER');
  const otherAnswer = subdomainConcern.questions?.[0].value;

  return (
    <VStack width={'100%'}>
      <Box position={'absolute'} left={9} top={8} onClick={resetSelectedSubdomainConcern} cursor={'pointer'}>
        <SvgIcon iconPath={svgIcons.chevronLeft} color={text.darkBlue} size={18} />
      </Box>
      <Box height={'30px'} width={'90%'} position={'relative'}>
        <Box top={-3} position={'absolute'} width={'100%'}>
          <Text variant={'loraSmallTitle'} color={text.mediumBlue}>
            {WELLNESS_SUB_DOMAINS_DISPLAY_LABELS[subdomainConcern.subdomain]}
          </Text>
        </Box>
      </Box>

      <VStack width={'80%'} gap={5} paddingBottom={'30px'}>
        <SectionContainer>
          <YourScore score={subdomainConcern.score * WELLNESS_DATA_MULTIPLIER} horizontal svgId={'subdomain'} />
        </SectionContainer>

        {isLoading ? (
          <>
            <LoadingSection />
            <LoadingSection />
            <LoadingSection />
            <LoadingSection />
          </>
        ) : (
          <>
            {isOther && otherAnswer && (
              <SectionContainer>
                <Text variant={'loraExtraSmallTitle'} fontWeight={500} color={text.mediumGray}>
                  Your Concern
                </Text>
                <Text variant={'urbanistMedium18'} color={text.mediumBlue} textAlign={'start'}>
                  {otherAnswer}
                </Text>
              </SectionContainer>
            )}

            <SectionContainer>
              <Text variant={'loraExtraSmallTitle'} color={text.mediumGray}>
                What this can mean for you
              </Text>
              <Text variant={'urbanistMedium18'} color={text.mediumBlue} textAlign={'start'} whiteSpace={'pre-wrap'}>
                {data?.meaning}
              </Text>
            </SectionContainer>

            <SectionContainer>
              <Text variant={'loraExtraSmallTitle'} color={text.mediumGray}>
                Why This Matters
              </Text>
              <Text variant={'urbanistMedium18'} color={text.mediumBlue} textAlign={'start'} whiteSpace={'pre-wrap'}>
                {data?.matters}
              </Text>
            </SectionContainer>

            <SectionContainer>
              <VStack alignItems={'start'}>
                <Text variant={'loraExtraSmallTitle'} color={text.mediumBlue}>
                  Recommended Micro-Skills & Learning Units
                </Text>
                {data?.note && data.note.length > 0 && (
                  <HStack width={'100%'} justify={'space-between'} alignItems={'center'} marginBottom={'10px'}>
                    <Center
                      padding={'8px'}
                      backgroundColor={extra.white}
                      rounded={'50%'}
                      style={{ height: 30, width: 30, boxShadow: '0px 1.85px 9.26px 0px #00417933' }}
                    >
                      <CircledExclamation color={extra.red} size={16} />
                    </Center>
                    <Text
                      variant={'urbanistSemiBold'}
                      color={text.mediumBlue}
                      style={{ maxWidth: '92%', fontSize: 12 }} // TODO: kol'ko width?
                      textAlign={'start'}
                    >
                      Note: {data.note}
                    </Text>
                  </HStack>
                )}
                <VStack gap={3} alignItems={'start'}>
                  {data?.recommendations.map((recommendation) => (
                    <SubdomainRecommendationSection key={recommendation.title} recommendation={recommendation} />
                  ))}
                </VStack>
              </VStack>
            </SectionContainer>

            {data?.next && (
              <SectionContainer>
                <Text variant={'loraExtraSmallTitle'} color={primary[500]}>
                  Your Next Step
                </Text>
                <Text variant={'urbanistMedium18'} color={text.mediumBlue} textAlign={'start'}>
                  {data?.next}
                </Text>
              </SectionContainer>
            )}
          </>
        )}
      </VStack>
    </VStack>
  );
};

export default SubdomainModalContent;
